import type {
  CreateShootingProductDto,
  CreateShootingResponse,
} from "@/types/mel";
import {
  fetchApi,
  fetchRest,
  getApiBaseUrl,
  getStorageFileUrl,
} from "@/config/api";

const API_URL = getApiBaseUrl();

export const maxProductPhotoSizeBytes = 15 * 1024 * 1024;
export const maxProductPhotoSizeLabel = "15 Mo";

export const supportedProductPhotoMimeTypes = new Set<string>([
  "image/jpeg",
  "image/pjpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/x-adobe-dng",
  "image/dng",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

export const supportedProductPhotoExtensions = new Set<string>([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "dng",
  "heic",
  "heif",
]);

export const supportedProductPhotoFormatsLabel =
  "JPG, PNG, GIF, WEBP, DNG, HEIF, HEIC";

export const productPhotoInputAccept =
  "image/jpeg,image/png,image/gif,image/webp,.dng,.heif,.heic";

export const getUnsupportedProductPhotoErrorMessage = () =>
  `Ce format n'est pas pris en charge. Formats supportés : ${supportedProductPhotoFormatsLabel}. Veuillez convertir votre fichier avant l'envoi.`;

export const getProductPhotoTooLargeErrorMessage = () =>
  `Fichier trop volumineux. Taille maximale : ${maxProductPhotoSizeLabel} par photo.`;

export const isSupportedProductPhotoSize = (file: { size: number }) =>
  file.size <= maxProductPhotoSizeBytes;

export const isSupportedProductPhotoFormat = (file: {
  type?: string;
  name: string;
}): boolean => {
  const fileType = (file.type || "").toLowerCase();
  if (fileType) {
    return supportedProductPhotoMimeTypes.has(fileType);
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return supportedProductPhotoExtensions.has(extension);
};

/**
 * Analyse un fichier Excel pour la demande de mise en ligne
 */
export const analyzeExcelFile = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await fetchRest(`${API_URL}/shootings/analyze-excel`, {
      method: "POST",
      body: formData,
      credentials: "include", // Pour envoyer les cookies
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de l'analyse du fichier Excel",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de l'analyse du fichier Excel");
  }
};

/**
 * Confirme l'upload Excel et crée le shooting en une seule transaction
 */
export const confirmExcelUpload = async (
  file: File,
): Promise<CreateShootingResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await fetchRest(`${API_URL}/shootings/create-from-excel`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la création du shooting",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la création du shooting depuis Excel");
  }
};

/**
 * Crée un shooting depuis le formulaire en ligne (produits en mémoire)
 */
export const createShootingFromForm = async (
  products: CreateShootingProductDto[],
): Promise<CreateShootingResponse> => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/create-from-form`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ products }),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la création du shooting",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "Erreur lors de la création du shooting depuis le formulaire",
    );
  }
};

/**
 * Récupère les caractéristiques pour une catégorie donnée
 */
export const getCaracteristiques = async (idCategorie: string) => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/get-caracteristiques`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idCategorie }),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          "Erreur lors de la récupération des caractéristiques",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération des caractéristiques");
  }
};

/**
 * Récupère toutes les caractéristiques disponibles (pour template)
 */
export const getAllCaracteristiques = async () => {
  try {
    const query = `
      query GetAllCaracteristiques {
        caracteristiques(lang: "fr") {
          id_caracteristique
          libelle
        }
      }
    `;

    const response = await fetchApi<{
      caracteristiques: Array<{ id_caracteristique: number; libelle: string }>;
    }>(query);

    return {
      caracteristiques: response.caracteristiques,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération des caractéristiques");
  }
};

export const getReferenceData = async () => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/get-reference-data`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          "Erreur lors de la récupération des données de référence",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération des données de référence");
  }
};

export const getCompositionAutocomplete = async (
  term: string,
  idString?: string,
) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/get-composition-autocomplete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ term, idString }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la récupération des compositions",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la récupération des compositions");
  }
};

// Sauvegarder les choix MEL
export const saveMelChoice = async (data: {
  melOption: "upload" | "treatment" | "shooting";
  prestation?: "cintre" | "efashion" | "libredroit";
  shootAllColors?: boolean;
  dataSource: "excel" | "form";
  products?: any[];
  references?: any[];
}) => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/save-mel-choice`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la sauvegarde du choix MEL",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la sauvegarde du choix MEL");
  }
};

// Sauvegarder les produits en brouillon (draft) - comme l'ancien PHP
export const saveMelDraft = async (data: {
  dataSource: "excel" | "form";
  products?: any[];
  references?: any[];
}) => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/save-mel-draft`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la sauvegarde du brouillon",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la sauvegarde du brouillon");
  }
};

// Récupérer les produits en brouillon (draft) du vendeur
export const getMelDraft = async () => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/mel-draft`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la récupération du brouillon",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la récupération du brouillon");
  }
};

// Mettre à jour un produit en brouillon (draft) existant
export const updateMelDraft = async (
  productId: number,
  data: {
    dataSource: "form";
    reference: any;
  },
) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/update-mel-draft/${productId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la mise à jour du brouillon",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la mise à jour du brouillon");
  }
};

// Supprimer un produit en brouillon (draft)
export const deleteMelDraft = async (productId: number) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/mel-draft/${productId}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la suppression du brouillon",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la suppression du brouillon");
  }
};

// Supprimer un produit en brouillon par sa référence (string)
export const deleteMelDraftByReference = async (reference: string) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/mel-draft-by-reference/${encodeURIComponent(
        reference,
      )}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la suppression du brouillon",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la suppression du brouillon");
  }
};

// Supprimer tous les brouillons du vendeur
export const deleteAllMelDrafts = async () => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/mel-drafts`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la suppression des brouillons",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la suppression des brouillons");
  }
};

/**
 * Vérifie si une référence existe déjà dans la liste des produits du vendeur
 * @param reference - La référence à vérifier
 * @returns { exists: boolean, reference: string }
 */
export const checkReferenceExists = async (
  reference: string,
  venduPar?: string,
): Promise<{ exists: boolean; reference: string }> => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/check-reference-exists`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ reference, venduPar }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la vérification de la référence",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur inconnue lors de la vérification de la référence");
  }
};

/**
 * Vérifie si plusieurs références existent déjà dans la liste des produits du vendeur (batch)
 */
export const checkReferencesExistsBatch = async (
  references: { reference: string; venduPar?: string }[],
): Promise<{ results: { reference: string; exists: boolean }[] }> => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/check-references-exists-batch`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ references }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la vérification des références",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("Erreur inconnue lors de la vérification des références");
  }
};

/**
 * Récupère les shootings du vendeur connecté avec pagination
 * @param page - Numéro de la page (défaut: 1)
 * @param limit - Nombre d'items par page (défaut: 25)
 */
export const getMyShootings = async (
  page = 1,
  limit = 25,
  idShooting = "",
  reference = "",
  toPrepare = false,
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(idShooting && { idShooting }),
      ...(reference && { reference }),
      ...(toPrepare && { toPrepare: "1" }),
    });

    const response = await fetchRest(
      `${API_URL}/shootings/my-shootings?${params}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la récupération des shootings",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération des shootings");
  }
};

export const getShootingPickupLabelUrl = (shootingId: number) =>
  `${API_URL}/shootings/pickup-label/${shootingId}`;

export const getShootingPickupLabelConfiguration = async (
  shootingId: number,
) => {
  const response = await fetchRest(
    `${API_URL}/shootings/pickup-label-configuration/${shootingId}`,
    { method: "GET", credentials: "include" },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || "Impossible de charger les informations de réception",
    );
  }
  return response.json();
};

export const saveShootingPickupLabelConfiguration = async (
  shootingId: number,
  data: {
    packages: Array<{ typeId: number; quantity: number }>;
    hangerTypeId: number;
  },
) => {
  const response = await fetchRest(
    `${API_URL}/shootings/pickup-label-configuration/${shootingId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || "Impossible d’enregistrer les informations de réception",
    );
  }
  return response.json();
};

/**
 * Récupère un shooting spécifique avec tous ses produits pour édition
 * @param shootingId - ID du shooting
 * @param options.flatProduits - si true, une ligne par `id_produit` (comme l’ancien PHP catalogue-upload), sans regroupement par `id_couleur_liee`
 * @param options.page / limit / q - pagination + filtre référence (uniquement avec flatProduits)
 */
export const getShootingForEdit = async (
  shootingId: number,
  options?: {
    flatProduits?: boolean;
    page?: number;
    limit?: number;
    q?: string;
  },
) => {
  try {
    const params = new URLSearchParams();
    if (options?.flatProduits) {
      params.set("flatProduits", "1");
      if (options.page != null) params.set("page", String(options.page));
      if (options.limit != null) params.set("limit", String(options.limit));
      if (options.q?.trim()) params.set("q", options.q.trim());
    }
    const qs = params.toString();
    const response = await fetchRest(
      `${API_URL}/shootings/shooting/${shootingId}${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la récupération du shooting",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération du shooting");
  }
};

/**
 * Télécharge le ZIP des photos d'un shooting
 * @param shootingId - ID du shooting
 */
export const downloadShootingPhotos = async (shootingId: number) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/download-photos/${shootingId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors du téléchargement des photos",
      );
    }

    const data = await response.json();
    if (!data.url) {
      throw new Error("URL storage absente");
    }
    window.open(getStorageFileUrl(data.url), "_blank");

    return { success: true };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors du téléchargement des photos");
  }
};

/**
 * Télécharge le ZIP des photos d'un seul produit
 * @param productId - ID du produit
 */
export const downloadProductPhotos = async (productId: number) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/download-photos/product/${productId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors du téléchargement des photos",
      );
    }

    const data = await response.json();
    if (!data.url) {
      throw new Error("URL storage absente");
    }
    window.open(getStorageFileUrl(data.url), "_blank");

    return { success: true };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors du téléchargement des photos");
  }
};

/**
 * Récupère la liste des modèles filtrés par catégories
 */
export const getModels = async (categories?: number[]) => {
  try {
    const url =
      categories && categories.length > 0
        ? `${API_URL}/shootings/models?categories=${categories.join(",")}`
        : `${API_URL}/shootings/models`;

    const response = await fetchRest(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la récupération des modèles",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération des modèles");
  }
};

/**
 * Assigne un modèle à un shooting
 */
export const assignModel = async (
  shootingId: number,
  idModel: number | null,
) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/${shootingId}/assign-model`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ idModel }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de l'assignation du modèle",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de l'assignation du modèle");
  }
};

/**
 * Confirme un shooting (date choisie par l'admin) — réservé aux admins.
 */
export const confirmerShooting = async (
  shootingId: number,
  dateShooting: string,
) => {
  try {
    const response = await fetch(
      `${API_URL}/shootings/${shootingId}/confirmer`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ dateShooting }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la confirmation du shooting",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la confirmation du shooting");
  }
};

/**
 * Finalise (ou annule la finalisation d') un shooting — réservé aux admins.
 * finalisation = "1" : finalise et génère la facture fournisseur.
 * finalisation = "0" : annule la finalisation.
 */
export const finaliserShooting = async (
  shootingId: number,
  finalisation: "0" | "1" = "1",
) => {
  try {
    const response = await fetch(
      `${API_URL}/shootings/${shootingId}/finaliser`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ finalisation }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la finalisation du shooting",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la finalisation du shooting");
  }
};

export const assignModelToProducts = async (
  productIds: number[],
  idModel: number | null,
) => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/assign-model/bulk`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ productIds, idModel }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          "Erreur lors de l'assignation du modèle aux références",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de l'assignation du modèle aux références");
  }
};

/**
 * Supprime un produit et tous ses produits liés (soft delete)
 */
export const deleteProduct = async (idProduit: number) => {
  try {
    const response = await fetchRest(
      `${API_URL}/shootings/product/${idProduit}/delete`,
      {
        method: "POST",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la suppression du produit",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la suppression du produit");
  }
};

/**
 * Met à jour un produit dans un shooting
 */
export const updateProduct = async (idProduit: number, productData: any) => {
  try {
    const response = await fetchRest(`${API_URL}/shootings/product/${idProduit}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Erreur lors de la mise à jour du produit",
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la mise à jour du produit");
  }
};

type ProductPhotoResponse = {
  success: boolean;
  message?: string;
  photos?: string[];
  nbPhotos?: number;
};

/**
 * Rotate an image file by the given degrees using Canvas
 */
const rotateImageFile = async (file: File, degrees: number): Promise<File> => {
  if (degrees === 0) return file;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Cannot get canvas context"));
        return;
      }

      // For 90 or 270 degrees, swap width and height
      const isVertical = degrees === 90 || degrees === 270;
      canvas.width = isVertical ? img.height : img.width;
      canvas.height = isVertical ? img.width : img.height;

      // Move to center, rotate, then draw
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to create blob"));
            return;
          }
          resolve(
            new File([blob], file.name, { type: file.type || "image/jpeg" }),
          );
        },
        file.type || "image/jpeg",
        0.95,
      );
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
};

export const uploadProductPhotos = async (
  productId: number,
  files: File[],
  rotations?: number[],
) => {
  if (!files || files.length === 0) {
    throw new Error("Aucun fichier sélectionné");
  }

  if (files.some((file) => !isSupportedProductPhotoSize(file))) {
    throw new Error(getProductPhotoTooLargeErrorMessage());
  }

  if (files.some((file) => !isSupportedProductPhotoFormat(file))) {
    throw new Error(getUnsupportedProductPhotoErrorMessage());
  }

  // Apply rotations to files if provided
  const processedFiles = await Promise.all(
    files.map((file, index) => {
      const rotation = rotations?.[index] || 0;
      return rotateImageFile(file, rotation);
    }),
  );

  const formData = new FormData();
  processedFiles.forEach((file) => formData.append("photos", file));
  formData.append("productId", productId.toString());

  try {
    const response = await fetchRest(`${API_URL}/api/upload-product-photo`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    const data: ProductPhotoResponse = await response
      .json()
      .catch(() => ({ success: false }));

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Erreur lors de l’upload des photos");
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de l’upload des photos");
  }
};

/** Multer limite à 10 fichiers par requête sur `POST /api/upload-product-photo`. */
export const maxPhotosPerProductUploadRequest = 10;

export const uploadProductPhotosBatched = async (
  productId: number,
  files: File[],
): Promise<ProductPhotoResponse> => {
  if (!files.length) {
    throw new Error("Aucun fichier sélectionné");
  }
  let last: ProductPhotoResponse = { success: false };
  for (let i = 0; i < files.length; i += maxPhotosPerProductUploadRequest) {
    const chunk = files.slice(i, i + maxPhotosPerProductUploadRequest);
    last = await uploadProductPhotos(productId, chunk);
    if (!last.success) {
      throw new Error(last.message || "Erreur lors de l’upload des photos");
    }
  }
  return last;
};

const MAX_ZIP_BULK_SIZE_BYTES = 100 * 1024 * 1024; // 100 Mo

export interface UploadBulkZipResponse {
  success: boolean;
  message: string;
  processed?: number;
  errors?: string[];
}

/**
 * Upload groupé par ZIP : pas de dossiers, les photos sont lues directement.
 * Nom des fichiers : {reference-couleur}-{position}.ext (ex: REF-NOIR-0.jpg). Max 100 Mo.
 */
export const uploadProductPhotosBulkZip = async (
  file: File,
): Promise<UploadBulkZipResponse> => {
  if (!file || file.size === 0) {
    throw new Error("Aucun fichier sélectionné");
  }
  if (file.size > MAX_ZIP_BULK_SIZE_BYTES) {
    throw new Error(
      `Fichier trop volumineux. Taille max : 100 Mo. Reçu : ${Math.round(
        file.size / 1024 / 1024,
      )} Mo.`,
    );
  }
  const formData = new FormData();
  formData.append("zip", file);

  const response = await fetchRest(
    `${API_URL}/api/upload-product-photos-bulk-zip`,
    {
      method: "POST",
      body: formData,
      credentials: "include",
    },
  );

  const data: UploadBulkZipResponse = await response
    .json()
    .catch(() => ({ success: false, message: "Réponse invalide" }));

  if (!response.ok) {
    throw new Error(data.message || "Erreur lors du traitement du ZIP");
  }
  return data;
};

/**
 * Récupère les photos de plusieurs produits en un seul appel.
 * @param productIds - IDs des produits
 * @param full - si true, retourne toutes les photos (c + z-1, z-2, ...) pour la gestion avancée ; sinon une seule (c) pour la liste
 */
export const getProductPhotosBatch = async (
  productIds: number[],
  full?: boolean,
): Promise<{ success: boolean; photosByProduct: Record<number, string[]> }> => {
  if (!productIds?.length) {
    return { success: true, photosByProduct: {} };
  }
  const ids = [...new Set(productIds)]
    .filter((id) => Number.isFinite(id) && id > 0)
    .slice(0, 200)
    .join(",");
  try {
    const params = new URLSearchParams({ productIds: ids });
    if (full) params.set("full", "true");
    const url = `${API_URL}/api/product-photos/batch?${params.toString()}`;
    const response = await fetchRest(url, {
      method: "GET",
      credentials: "include",
    });
    const text = await response.text().catch(() => "");
    const data = text
      ? JSON.parse(text)
      : { success: false, photosByProduct: {} };
    if (!response.ok) {
      throw new Error(
        data.message || "Erreur lors de la récupération des photos",
      );
    }
    const photosByProduct: Record<number, string[]> = {};
    const raw = data.photosByProduct ?? {};
    for (const [k, v] of Object.entries(raw)) {
      const id = Number(k);
      if (Number.isFinite(id) && Array.isArray(v)) {
        photosByProduct[id] = v;
      }
    }
    return { success: true, photosByProduct };
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("Erreur lors de la récupération des photos");
  }
};

/** size: 'accueil' pour la liste (défaut), 'fiche' pour la modal fiche produit */
export const getProductPhotos = async (
  productId: number,
  size?: "accueil" | "fiche",
) => {
  try {
    // Sanitize productId to avoid accidental trailing characters like '?' in the URL
    const idStr = String(productId)
      .trim()
      .replace(/[^0-9]/g, "");
    const cleanId = Number.parseInt(idStr, 10);
    if (!Number.isFinite(cleanId) || cleanId <= 0) {
      throw new Error("Identifiant produit invalide");
    }

    const url = new URL(
      `${API_URL}/api/product-photos/${encodeURIComponent(String(cleanId))}`,
    );
    if (size === "fiche") url.searchParams.set("size", "fiche");

    const response = await fetchRest(url.toString(), {
      method: "GET",
      credentials: "include",
    });

    // Some proxies might return empty body on 204; make parsing resilient
    const text = await response.text().catch(() => "");
    const data: ProductPhotoResponse = text
      ? JSON.parse(text)
      : { success: false };

    if (!response.ok || !data.success) {
      throw new Error(
        data.message || "Erreur lors de la récupération des photos",
      );
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la récupération des photos");
  }
};

export const deleteProductPhoto = async (
  productId: number,
  filename: string,
) => {
  try {
    const response = await fetchRest(`${API_URL}/api/product-photo/delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        productId,
        filename,
      }),
    });

    const data: ProductPhotoResponse = await response
      .json()
      .catch(() => ({ success: false }));

    if (!response.ok || !data.success) {
      throw new Error(
        data.message || "Erreur lors de la suppression de la photo",
      );
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la suppression de la photo");
  }
};

export const rotateProductPhoto = async (
  productId: number,
  filename: string,
  direction: "clockwise" | "counterclockwise" = "clockwise",
) => {
  try {
    const response = await fetchRest(`${API_URL}/api/product-photo/rotate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        productId,
        filename,
        direction,
      }),
    });

    const data: ProductPhotoResponse = await response
      .json()
      .catch(() => ({ success: false }));

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Erreur lors de la rotation de la photo");
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la rotation de la photo");
  }
};

export const reorderProductPhotos = async (
  productId: number,
  positions: string[],
): Promise<ProductPhotoResponse> => {
  try {
    const response = await fetchRest(`${API_URL}/api/product-photos/reorder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        productId,
        positions,
      }),
    });

    const data: ProductPhotoResponse = await response
      .json()
      .catch(() => ({ success: false }));

    if (!response.ok || !data.success) {
      throw new Error(
        data.message || "Erreur lors de la réorganisation des photos",
      );
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Erreur lors de la réorganisation des photos");
  }
};

/**
 * Récupère les produits des shootings UPGR avec pagination et filtres
 */
export const getUploadProducts = async (
  vendorId: number,
  filters?: {
    shootingIds?: number[];
    reference?: string;
    idShooting?: string;
    premelFilter?: "" | "en_ligne" | "brouillon";
    page?: number;
    limit?: number;
    sortBy?: string;
  },
) => {
  const variables: Record<string, any> = {
    vendorId,
  };

  if (filters?.shootingIds && filters.shootingIds.length > 0) {
    variables.shootingIds = filters.shootingIds;
  }
  if (filters?.reference) {
    variables.reference = filters.reference;
  }
  if (filters?.idShooting) {
    variables.idShooting = filters.idShooting;
  }
  if (filters?.premelFilter) {
    variables.premelFilter = filters.premelFilter;
  }
  variables.page = filters?.page ?? 1;
  variables.limit = filters?.limit ?? 30;
  if (filters?.sortBy) {
    variables.sortBy = filters.sortBy;
  }

  const query = `
    query GetUploadProducts(
      $vendorId: Int!
      $shootingIds: [Int!]
      $reference: String
      $idShooting: String
      $premelFilter: String
      $page: Int
      $limit: Int
      $sortBy: String
    ) {
      uploadProducts(
        vendorId: $vendorId
        shootingIds: $shootingIds
        reference: $reference
        idShooting: $idShooting
        premelFilter: $premelFilter
        page: $page
        limit: $limit
        sortBy: $sortBy
      ) {
        items {
          id
          reference
          referenceBase
          idShooting
          premel
          dateCreation
          dateModification
          nbPhotos
          idModel
          idCategorie
          idCollection
          shootingId
          shootingDateDemande
          main
        }
        total
        page
        limit
        totalPages
      }
    }
  `;

  const result = await fetchApi<{
    uploadProducts: {
      items: Array<{
        id: number;
        reference: string | null;
        referenceBase: string | null;
        idShooting: number | null;
        premel: string | null;
        dateCreation: string | null;
        dateModification: string | null;
        nbPhotos: number | null;
        idModel: number | null;
        idCategorie: number | null;
        idCollection: number | null;
        shootingId: number | null;
        shootingDateDemande: string | null;
        main: number | null;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>(query, variables);

  return result.uploadProducts;
};
